/**
 * Markdown Formatter - Layer 3.
 *
 * Transforms canonical TestRunResult into Markdown documentation.
 * Compatible with existing markdown output from framework reporters.
 */

import { diffStateValues, summarizeStateChanges } from "executable-stories-core";
import { summarizeAccumulation } from "executable-stories-core/utils/accumulation";
import { assertNever } from "executable-stories-core/utils/assert-never";
import { assertionState, assertiveSteps } from "executable-stories-core/utils/assertive-steps";
import type { StoryStep, DocEntry } from "executable-stories-core/types/story";
import type { FeatureDeclaration, TestRunResult, TestCaseResult, TestStatus } from "executable-stories-core/types/test-result";
import type { MarkdownRenderers } from "../types/options";
import { bySourcePosition, earliestSourceLine } from "./source-order";

/**
 * True for a local filesystem path (`/foo`, `\foo`, `C:\foo`) rather than a
 * URL. Screenshot/video paths are meant to be rewritten to a relative
 * `assets/...` path (or embedded as a `data:` URI) by capture-time inlining
 * or the report's asset bundler; a path that's still absolute by the time it
 * reaches this formatter means that never happened — most often because the
 * bundler doesn't run at all in this pipeline (e.g. Markdown posted straight
 * to a GitHub PR comment, with no asset-copy step in between). Embedding it
 * verbatim (`![alt](path)` / `<source src="path">`) would be a
 * guaranteed-broken reference wherever the Markdown ends up rendered.
 */
function isLocalFsPath(value: string): boolean {
  return /^(?:[/\\]|[A-Za-z]:[/\\])/.test(value);
}

/** Options for Markdown formatting */
export interface MarkdownOptions {
  /** Report title. Default: "User Stories" */
  title?: string;
  /** Include status icons on scenarios. Default: true */
  includeStatusIcons?: boolean;
  /** Include metadata table (date, version). Default: true */
  includeMetadata?: boolean;
  /** Include error details for failed scenarios. Default: true */
  includeErrors?: boolean;
  /** Scenario heading level. Default: 3 */
  scenarioHeadingLevel?: 2 | 3 | 4;
  /** Step rendering style. Default: "bullets" */
  stepStyle?: "bullets" | "gherkin";
  /** Group scenarios by. Default: "file" */
  groupBy?: "file" | "suite" | "none";
  /** Sort scenarios. Default: "source" */
  sortScenarios?: "alpha" | "source" | "none";
  /** Suite path separator. Default: " - " */
  suiteSeparator?: string;
  /** Include YAML front-matter for machine parsing. Default: false */
  includeFrontMatter?: boolean;
  /** Include summary table (counts, duration). Default: false */
  includeSummaryTable?: boolean;
  /** Base URL for source permalinks. E.g., "https://github.com/user/repo/blob" */
  permalinkBaseUrl?: string;
  /** URL template for ticket links. Use {ticket} as placeholder */
  ticketUrlTemplate?: string;
  /** URL template for trace links. Use {traceId} as placeholder. E.g. "https://grafana.example.com/explore?traceId={traceId}" */
  traceUrlTemplate?: string;
  /** Include source links when permalinkBaseUrl is set. Default: true */
  includeSourceLinks?: boolean;
  /** Custom renderers for doc entries */
  customRenderers?: MarkdownRenderers;
  /**
   * Write a local screenshot/video path as an ordinary markdown reference
   * instead of the "unavailable" line. Only for markdown handed to
   * `gh pr comment|create|edit --attach <path>` (GitHub CLI 2.99+), which
   * uploads the file and rewrites the reference. Anywhere else the reference
   * is dead, which is why this is off by default. Default: false
   */
  attachImages?: boolean;
  /**
   * Emit a stable in-page anchor before each scenario heading, so external tools
   * can deep-link to a scenario by fragment. Given a test case, return the anchor
   * id (without `#`), or undefined to skip. Off by default — only the living-docs
   * site opts in, so plain markdown output is unchanged.
   */
  scenarioAnchor?: (tc: TestCaseResult) => string | undefined;
  /**
   * Render a short badge line directly under a scenario heading (e.g. a
   * what's-changed marker like "🆕 New" / "⚠️ Regressed"). Return the markdown
   * line, or undefined to skip. Off by default — only the living-docs site opts in.
   */
  scenarioBadge?: (tc: TestCaseResult) => string | undefined;
  /**
   * Render a link line under a scenario heading pointing at hand-written context
   * (the scenario's note page). Return the markdown line, or undefined to skip.
   * Off by default — only the living-docs site opts in.
   */
  scenarioNoteLink?: (tc: TestCaseResult) => string | undefined;
}

/** Resolved options with all defaults */
type ResolvedMarkdownOptions = {
  title: string;
  includeStatusIcons: boolean;
  includeMetadata: boolean;
  includeErrors: boolean;
  scenarioHeadingLevel: 2 | 3 | 4;
  stepStyle: "bullets" | "gherkin";
  groupBy: "file" | "suite" | "none";
  sortScenarios: "alpha" | "source" | "none";
  suiteSeparator: string;
  includeFrontMatter: boolean;
  includeSummaryTable: boolean;
  permalinkBaseUrl?: string;
  ticketUrlTemplate?: string;
  traceUrlTemplate?: string;
  includeSourceLinks: boolean;
  customRenderers?: MarkdownRenderers;
  attachImages: boolean;
  scenarioAnchor?: (tc: TestCaseResult) => string | undefined;
  scenarioBadge?: (tc: TestCaseResult) => string | undefined;
  scenarioNoteLink?: (tc: TestCaseResult) => string | undefined;
};

/**
 * Markdown Formatter.
 *
 * Transforms TestRunResult into Markdown documentation that matches
 * the output format of existing framework reporters.
 */
export class MarkdownFormatter {
  private options: ResolvedMarkdownOptions;
  /**
   * Previous `state` snapshot per label lane (label ?? "") — reset per
   * scenario, mirroring storyboard lane semantics, so repeat captures render
   * a diff summary against the prior same-label snapshot.
   */
  private stateLanes = new Map<string, unknown>();

  constructor(options: MarkdownOptions = {}) {
    this.options = {
      title: options.title ?? "User Stories",
      includeStatusIcons: options.includeStatusIcons ?? true,
      includeMetadata: options.includeMetadata ?? true,
      includeErrors: options.includeErrors ?? true,
      scenarioHeadingLevel: options.scenarioHeadingLevel ?? 3,
      stepStyle: options.stepStyle ?? "bullets",
      groupBy: options.groupBy ?? "file",
      sortScenarios: options.sortScenarios ?? "source",
      suiteSeparator: options.suiteSeparator ?? " - ",
      includeFrontMatter: options.includeFrontMatter ?? false,
      includeSummaryTable: options.includeSummaryTable ?? false,
      permalinkBaseUrl: options.permalinkBaseUrl,
      ticketUrlTemplate: options.ticketUrlTemplate,
      traceUrlTemplate: options.traceUrlTemplate,
      includeSourceLinks: options.includeSourceLinks ?? true,
      customRenderers: options.customRenderers,
      attachImages: options.attachImages ?? false,
      scenarioAnchor: options.scenarioAnchor,
      scenarioBadge: options.scenarioBadge,
      scenarioNoteLink: options.scenarioNoteLink,
    };
  }

  /**
   * Format a test run into Markdown.
   *
   * @param run - Canonical test run result
   * @returns Markdown string
   */
  format(run: TestRunResult): string {
    const lines: string[] = [];

    // Front-matter
    if (this.options.includeFrontMatter) {
      this.renderFrontMatter(lines, run);
    }

    // Title
    lines.push(`# ${this.options.title}`);
    lines.push("");

    // Metadata
    if (this.options.includeMetadata) {
      this.renderMetadata(lines, run);
      lines.push("");
    }

    // Summary table
    if (this.options.includeSummaryTable) {
      this.renderSummaryTable(lines, run);
      lines.push("");
    }

    // Render scenarios based on grouping
    switch (this.options.groupBy) {
      case "none":
        this.renderFlatList(lines, run.testCases);
        break;
      case "suite":
        this.renderBySuite(lines, run.testCases);
        break;
      case "file":
      default:
        this.renderByFile(lines, run.testCases, run.features);
        break;
    }

    // Custom footer
    if (this.options.customRenderers?.renderFooter) {
      const footer = this.options.customRenderers.renderFooter(run);
      if (footer) {
        lines.push("");
        lines.push(footer);
      }
    }

    return lines.join("\n").trimEnd();
  }

  /**
   * Render YAML front-matter.
   */
  private renderFrontMatter(lines: string[], run: TestRunResult): void {
    const data: Record<string, unknown> = {
      title: this.options.title,
      generatedAt: new Date(run.startedAtMs).toISOString(),
      durationMs: run.durationMs,
      scenarios: run.testCases.length,
      passed: run.testCases.filter((tc) => tc.status === "passed").length,
      failed: run.testCases.filter((tc) => tc.status === "failed").length,
      skipped: run.testCases.filter((tc) => tc.status === "skipped").length,
      pending: run.testCases.filter((tc) => tc.status === "pending").length,
    };

    if (run.packageVersion) data.version = run.packageVersion;
    if (run.gitSha) data.gitSha = run.gitSha.length > 7 ? run.gitSha.slice(0, 7) : run.gitSha;
    if (run.coverage) data.coverage = run.coverage;

    lines.push("---");
    for (const [key, value] of Object.entries(data)) {
      if (value === undefined) continue;
      if (typeof value === "object") {
        lines.push(`${key}:`);
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
          lines.push(`  ${k}: ${v}`);
        }
      } else {
        lines.push(`${key}: ${value}`);
      }
    }
    lines.push("---");
    lines.push("");
  }

  /**
   * Render summary table.
   */
  private renderSummaryTable(lines: string[], run: TestRunResult): void {
    const totalScenarios = run.testCases.length;
    const totalSteps = run.testCases.reduce((acc, tc) => acc + tc.story.steps.length, 0);
    const passed = run.testCases.filter((tc) => tc.status === "passed").length;
    const failed = run.testCases.filter((tc) => tc.status === "failed").length;
    const skipped = run.testCases.filter((tc) => tc.status === "skipped").length;
    const pending = run.testCases.filter((tc) => tc.status === "pending").length;

    lines.push("| Scenarios | Steps | Passed | Failed | Skipped | Pending | Duration |");
    lines.push("| ---: | ---: | ---: | ---: | ---: | ---: | ---: |");
    lines.push(`| ${totalScenarios} | ${totalSteps} | ${passed} | ${failed} | ${skipped} | ${pending} | ${this.formatDuration(run.durationMs)} |`);

    // Coverage summary if available
    if (run.coverage) {
      lines.push("");
      lines.push("| Coverage | % |");
      lines.push("| --- | ---: |");
      if (run.coverage.statementsPct !== undefined) {
        lines.push(`| Statements | ${run.coverage.statementsPct}% |`);
      }
      if (run.coverage.branchesPct !== undefined) {
        lines.push(`| Branches | ${run.coverage.branchesPct}% |`);
      }
      if (run.coverage.functionsPct !== undefined) {
        lines.push(`| Functions | ${run.coverage.functionsPct}% |`);
      }
      if (run.coverage.linesPct !== undefined) {
        lines.push(`| Lines | ${run.coverage.linesPct}% |`);
      }
    }
  }

  /**
   * Format duration in human-readable form.
   */
  private formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  /**
   * Render metadata table.
   */
  private renderMetadata(lines: string[], run: TestRunResult): void {
    const rows: Array<[string, string]> = [];

    const startDate = new Date(run.startedAtMs);
    rows.push(["Date", startDate.toISOString()]);

    if (run.packageVersion) {
      rows.push(["Version", run.packageVersion]);
    }

    if (run.gitSha) {
      const shortSha = run.gitSha.length > 7 ? run.gitSha.slice(0, 7) : run.gitSha;
      rows.push(["Git SHA", shortSha]);
    }

    // Say so when this is a composite. Without it the only explanation for a
    // scenario that did not just run lives in state the reader cannot see.
    const accumulated = summarizeAccumulation(run.testCases);
    if (accumulated) {
      const oldest = new Date(accumulated.oldestRunAtMs).toISOString();
      const newest = new Date(accumulated.newestRunAtMs).toISOString();
      rows.push([
        "Source",
        `accumulated from ${accumulated.runs} runs (${oldest} to ${newest})`,
      ]);
    }

    if (rows.length > 0) {
      lines.push("| Key | Value |");
      lines.push("| --- | --- |");
      for (const [key, value] of rows) {
        lines.push(`| ${key} | ${value} |`);
      }
    }
  }

  /**
   * Render scenarios grouped by file.
   */
  private renderByFile(
    lines: string[],
    testCases: TestCaseResult[],
    features?: FeatureDeclaration[]
  ): void {
    const byFile = groupBy(testCases, (tc) => tc.sourceFile);
    const declarations = new Map((features ?? []).map((f) => [f.sourceFile, f]));

    for (const [file, fileTestCases] of byFile) {
      const declared = declarations.get(file);
      lines.push(`## ${declared ? formatFeatureHeading(declared) : file}`);
      lines.push("");

      if (declared) {
        this.renderFeatureDeclaration(lines, declared, file);
      }

      // Group by suite path within file
      this.renderSuiteGroups(lines, fileTestCases, 3);
    }
  }

  /**
   * Render what a feature is for, ahead of the scenarios that prove it.
   */
  private renderFeatureDeclaration(
    lines: string[],
    declared: FeatureDeclaration,
    file: string
  ): void {
    lines.push(`_${file}_`);
    lines.push("");

    if (declared.narrative) {
      lines.push(dedentNarrative(declared.narrative));
      lines.push("");
    }

    if (declared.glossary?.length) {
      for (const { term, definition } of declared.glossary) {
        lines.push(`- **${term}** — ${definition}`);
      }
      lines.push("");
    }
  }

  /**
   * Render scenarios grouped by suite path.
   */
  private renderBySuite(lines: string[], testCases: TestCaseResult[]): void {
    this.renderSuiteGroups(lines, testCases, 2);
  }

  /**
   * Render suite groups.
   */
  private renderSuiteGroups(
    lines: string[],
    testCases: TestCaseResult[],
    baseLevel: number
  ): void {
    const bySuite = groupBy(testCases, (tc) =>
      tc.titlePath.join(this.options.suiteSeparator)
    );

    // Sort suite groups
    const sortedSuites = this.sortSuiteGroups([...bySuite.entries()]);

    for (const [suitePath, suiteTestCases] of sortedSuites) {
      if (suitePath) {
        lines.push(`${"#".repeat(baseLevel)} ${suitePath}`);
        lines.push("");
      }

      const sorted = this.sortScenarios(suiteTestCases);
      for (const tc of sorted) {
        this.renderScenario(lines, tc);
      }
    }
  }

  /**
   * Render flat list of scenarios.
   */
  private renderFlatList(lines: string[], testCases: TestCaseResult[]): void {
    const sorted = this.sortScenarios(testCases);
    for (const tc of sorted) {
      this.renderScenario(lines, tc);
    }
  }

  /**
   * Render a single scenario.
   */
  private renderScenario(lines: string[], tc: TestCaseResult): void {
    // Stable deep-link target — an explicit anchor just above the heading, so a
    // fragment like `#scenario-foo` resolves regardless of how the renderer slugs
    // heading text. Opt-in (living-docs site only); see `scenarioAnchor` option.
    const anchorId = this.options.scenarioAnchor?.(tc);
    if (anchorId) {
      lines.push(`<a id="${anchorId}"></a>`);
      lines.push("");
    }

    // Check for custom scenario header renderer
    if (this.options.customRenderers?.renderScenarioHeader) {
      const custom = this.options.customRenderers.renderScenarioHeader(tc);
      if (custom !== null) {
        lines.push(custom);
        lines.push("");
        // Still render steps and docs after custom header
        this.renderScenarioBody(lines, tc);
        return;
      }
    }

    const headingPrefix = "#".repeat(this.options.scenarioHeadingLevel);

    // Status icon
    let icon = "";
    if (this.options.includeStatusIcons) {
      icon = this.getStatusIcon(tc.status) + " ";
    }

    // Scenario heading. Planned scenarios (it.todo) say so — canonical
    // status is "pending" but the reader should see intent, not limbo.
    const plannedSuffix = tc.rawStatus === "todo" ? " _(planned)_" : "";
    lines.push(`${headingPrefix} ${icon}${tc.story.scenario}${plannedSuffix}`);

    // What's-changed badge (opt-in) — sits directly under the heading.
    const badge = this.options.scenarioBadge?.(tc);
    if (badge) {
      lines.push(badge);
    }

    // Business-context link (opt-in) — points to the scenario's hand-written note.
    const noteLink = this.options.scenarioNoteLink?.(tc);
    if (noteLink) {
      lines.push(noteLink);
    }

    // Source link
    if (this.options.includeSourceLinks && this.options.permalinkBaseUrl && tc.sourceFile !== "unknown") {
      const permalink = this.buildPermalink(tc);
      lines.push(`Source: [${tc.sourceFile}](${permalink})`);
    }

    // Tags and tickets
    const meta: string[] = [];
    if (tc.tags.length > 0) {
      meta.push(`Tags: ${tc.tags.map((t) => `\`${t}\``).join(", ")}`);
    }
    if (tc.story.tickets && tc.story.tickets.length > 0) {
      const ticketTemplate = this.options.ticketUrlTemplate;
      const ticketLinks = tc.story.tickets.map((t) => {
        if (t.url) {
          return `[${t.id}](${t.url})`;
        }
        if (ticketTemplate) {
          return `[${t.id}](${ticketTemplate.replace("{ticket}", t.id)})`;
        }
        return `\`${t.id}\``;
      });
      meta.push(`Tickets: ${ticketLinks.join(", ")}`);
    }
    if (tc.rawStatus === "timeout" || tc.rawStatus === "interrupted") {
      meta.push(`Outcome: \`${tc.rawStatus}\``);
    }
    // Trace context (injected by OTel bridge in story.init())
    const otelMeta = (tc.story.meta as Record<string, unknown> | undefined)
      ?.otel as { traceId?: string } | undefined;
    if (otelMeta?.traceId) {
      const traceTemplate = this.options.traceUrlTemplate;
      if (traceTemplate) {
        const url = traceTemplate.replace(/\{traceId\}/g, otelMeta.traceId);
        meta.push(
          `Trace: [${otelMeta.traceId.slice(0, 16)}…](${url})`,
        );
      } else {
        meta.push(`Trace: \`${otelMeta.traceId}\``);
      }
    }

    if (meta.length > 0) {
      lines.push(meta.join(" | "));
    }

    lines.push("");

    this.renderScenarioBody(lines, tc);
  }

  /**
   * Render scenario body (docs, steps, errors).
   */
  private renderScenarioBody(lines: string[], tc: TestCaseResult): void {
    // State diffs are scoped to one scenario.
    this.stateLanes = new Map();

    // Story-level docs
    if (tc.story.docs && tc.story.docs.length > 0) {
      for (const doc of tc.story.docs) {
        this.renderDocEntry(lines, doc);
      }
    }

    // Steps
    // A scenario whose claim checked nothing is flagged in the prose, on the
    // steps that make the claim. Judged per scenario rather than per step: a
    // marker credits its trailing assertion to one step, so a scenario that
    // declares two claims and checks once would otherwise flag the first for
    // nothing. Same rule the grade and the CLI summary use, so the report tells
    // one story.
    const unasserted = new Set(
      assertionState(tc.story.steps) === "unasserted"
        ? assertiveSteps(tc.story.steps)
        : []
    );
    for (const step of tc.story.steps) {
      this.renderStep(lines, step, unasserted.has(step));
    }

    // Error
    if (tc.status === "failed" && tc.errorMessage && this.options.includeErrors) {
      lines.push("**Failure**");
      lines.push("");
      lines.push("```text");
      lines.push(tc.errorMessage);
      if (tc.errorStack) {
        lines.push("");
        lines.push(tc.errorStack);
      }
      lines.push("```");
      lines.push("");
    }

    lines.push("");
  }

  /**
   * Build permalink URL for a test case.
   */
  private buildPermalink(tc: TestCaseResult): string {
    const base = this.options.permalinkBaseUrl!.replace(/\/$/, "");
    const file = tc.sourceFile;
    const line = tc.sourceLine > 0 ? `#L${tc.sourceLine}` : "";
    return `${base}/${file}${line}`;
  }

  /**
   * Render a step.
   */
  private renderStep(lines: string[], step: StoryStep, unasserted = false): void {
    // Check for custom step renderer
    if (this.options.customRenderers?.renderStep) {
      const custom = this.options.customRenderers.renderStep(step);
      if (custom !== null) {
        lines.push(custom);
        // Still render step docs
        if (step.docs && step.docs.length > 0) {
          const indent = this.options.stepStyle === "gherkin" ? "" : "    ";
          for (const doc of step.docs) {
            this.renderDocEntry(lines, doc, indent);
          }
        }
        return;
      }
    }

    // Mode indicator
    let modeIndicator = "";
    if (step.mode === "skip") {
      modeIndicator = " _(skipped)_";
    } else if (step.mode === "todo") {
      modeIndicator = " _(todo)_";
    } else if (step.mode === "fails") {
      modeIndicator = " _(expected to fail)_";
    }
    // Reads as part of the sentence rather than a badge: the claim itself has
    // to look unbacked, or the reader takes it at face value.
    if (unasserted) modeIndicator += " _(no assertion)_";

    if (this.options.stepStyle === "gherkin") {
      lines.push(`**${step.keyword}** ${step.text}${modeIndicator}`);
    } else {
      lines.push(`- **${step.keyword}** ${step.text}${modeIndicator}`);
    }

    // Render step docs
    if (step.docs && step.docs.length > 0) {
      const indent = this.options.stepStyle === "gherkin" ? "" : "    ";
      for (const doc of step.docs) {
        this.renderDocEntry(lines, doc, indent);
      }
    }
  }

  /**
   * Render a documentation entry.
   */
  private renderDocEntry(lines: string[], entry: DocEntry, indent = ""): void {
    // Check for custom doc entry renderer
    if (this.options.customRenderers?.renderDocEntry) {
      const custom = this.options.customRenderers.renderDocEntry(entry);
      if (custom !== null) {
        lines.push(`${indent}${custom}`);
        return;
      }
    }

    switch (entry.kind) {
      case "note":
        lines.push(`${indent}> ${entry.text}`);
        break;

      case "tag":
        lines.push(`${indent}${entry.names.map((n) => `\`${n}\``).join(" ")}`);
        break;

      case "kv": {
        const val = typeof entry.value === "string"
          ? entry.value
          : JSON.stringify(entry.value);
        lines.push(`${indent}- **${entry.label}:** ${val}`);
        break;
      }

      case "code":
        if (entry.label) {
          lines.push(`${indent}**${entry.label}**`);
          lines.push(`${indent}`);
        }
        lines.push(`${indent}\`\`\`${entry.lang ?? ""}`);
        for (const line of (entry.content ?? "").split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}\`\`\``);
        lines.push(`${indent}`);
        break;

      case "table":
        if (entry.label) {
          lines.push(`${indent}**${entry.label}**`);
          lines.push(`${indent}`);
        }
        lines.push(`${indent}| ${entry.columns.join(" | ")} |`);
        lines.push(`${indent}| ${entry.columns.map(() => "---").join(" | ")} |`);
        for (const row of entry.rows) {
          lines.push(`${indent}| ${row.join(" | ")} |`);
        }
        lines.push(`${indent}`);
        break;

      case "link":
        lines.push(`${indent}[${entry.label}](${entry.url})`);
        break;

      case "section":
        lines.push(`${indent}**${entry.title}**`);
        lines.push(`${indent}`);
        for (const line of dedentNarrative(entry.markdown ?? "").split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}`);
        break;

      case "mermaid":
        if (entry.title) {
          lines.push(`${indent}**${entry.title}**`);
        }
        lines.push(`${indent}\`\`\`mermaid`);
        for (const line of (entry.code ?? "").split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}\`\`\``);
        break;

      case "screenshot":
        // Only `data:`/`http(s):` sources, or a relative path a bundler is
        // meant to resolve, render as an embedded image.
        if (isLocalFsPath(entry.path) && !this.options.attachImages) {
          lines.push(`${indent}*Screenshot unavailable${entry.alt ? ` — ${entry.alt}` : ""} (\`${entry.path}\` was not readable when the report was generated)*`);
        } else {
          lines.push(`${indent}![${entry.alt ?? "Screenshot"}](${entry.path})`);
        }
        break;

      case "video": {
        // Raw HTML so a real, playable clip renders inline — Markdown and
        // Astro/Starlight MDX both pass <video> through untouched. The asset
        // bundler scans <source src> and copies the file into the docs site.
        // Video is never inlined as a data URI (bytes are too large), so an
        // absolute path here — unlike screenshot, which at least sometimes
        // inlines successfully — means it will *always* be broken wherever
        // Markdown reaches an audience without that bundling step in between
        // (e.g. posted straight to a GitHub PR comment).
        if (isLocalFsPath(entry.path)) {
          if (!this.options.attachImages) {
            lines.push(`${indent}*Video unavailable${entry.caption ? ` — ${entry.caption}` : ""} (\`${entry.path}\` was not readable when the report was generated)*`);
            break;
          }
          // gh rewrites markdown image syntax, and GitHub plays an uploaded
          // clip from that same reference, so `<video>` is not the shape to
          // write here.
          lines.push(`${indent}![${entry.caption ?? "Video"}](${entry.path})`);
          break;
        }
        const poster = entry.poster ? ` poster="${entry.poster}"` : "";
        lines.push(`${indent}`);
        lines.push(`${indent}<video controls preload="metadata"${poster} class="doc-video">`);
        lines.push(`${indent}  <source src="${entry.path}" />`);
        lines.push(`${indent}</video>`);
        if (entry.caption) {
          lines.push(`${indent}`);
          lines.push(`${indent}*${entry.caption}*`);
        }
        lines.push(`${indent}`);
        break;
      }

      case "html": {
        // The sandboxed iframe is HTML-report-only. Markdown degrades to a
        // link for url/path sources; inline content goes in a collapsible
        // fenced block (dumping live HTML into MD is unpredictable across
        // renderers, and GitHub strips iframes anyway).
        const htmlLabel = entry.title ?? "Embedded HTML";
        if (entry.url !== undefined || entry.path !== undefined) {
          lines.push(`${indent}[${htmlLabel}](${entry.url ?? entry.path})`);
          break;
        }
        lines.push(`${indent}<details>`);
        lines.push(`${indent}<summary>${htmlLabel}</summary>`);
        lines.push(`${indent}`);
        lines.push(`${indent}\`\`\`html`);
        for (const line of (entry.content ?? "").split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}\`\`\``);
        lines.push(`${indent}`);
        lines.push(`${indent}</details>`);
        lines.push(`${indent}`);
        break;
      }

      case "state": {
        const lane = entry.label ?? "";
        lines.push(`${indent}**${entry.label ?? "State"}**`);
        if (this.stateLanes.has(lane)) {
          const summary = summarizeStateChanges(diffStateValues(this.stateLanes.get(lane), entry.value));
          for (const line of summary) lines.push(`${indent}- ${line}`);
        }
        this.stateLanes.set(lane, entry.value);
        lines.push(`${indent}`);
        lines.push(`${indent}<details>`);
        lines.push(`${indent}<summary>snapshot</summary>`);
        lines.push(`${indent}`);
        lines.push(`${indent}\`\`\`json`);
        for (const line of JSON.stringify(entry.value ?? null, null, 2).split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}\`\`\``);
        lines.push(`${indent}`);
        lines.push(`${indent}</details>`);
        lines.push(`${indent}`);
        break;
      }

      case "custom":
        if (entry.type === "visual" && entry.data && typeof entry.data === "object") {
          const data = entry.data as Record<string, unknown>;
          const status = typeof data.status === "string" ? data.status : "unknown";
          lines.push(`${indent}**Visual Check** (${status})`);
          if (typeof data.baseline === "string") lines.push(`${indent}- Baseline: ${data.baseline}`);
          if (typeof data.actual === "string") lines.push(`${indent}- Actual: ${data.actual}`);
          if (typeof data.diff === "string") lines.push(`${indent}- Diff: ${data.diff}`);
          lines.push(`${indent}`);
          break;
        }
        lines.push(`${indent}**[${entry.type}]**`);
        lines.push(`${indent}`);
        lines.push(`${indent}\`\`\`json`);
        for (const line of JSON.stringify(entry.data ?? null, null, 2).split("\n")) {
          lines.push(`${indent}${line}`);
        }
        lines.push(`${indent}\`\`\``);
        lines.push(`${indent}`);
        break;

      default:
        assertNever(entry, "Markdown: unhandled doc kind");
    }

    // Render children with increased indentation
    if (Array.isArray(entry.children) && entry.children.length > 0) {
      const childIndent = indent + "  ";
      for (const child of entry.children) {
        this.renderDocEntry(lines, child, childIndent);
      }
    }
  }

  /**
   * Get status icon for a status.
   */
  private getStatusIcon(status: TestStatus): string {
    switch (status) {
      case "passed":
        return "✅";
      case "failed":
        return "❌";
      case "skipped":
        return "⏩";
      case "pending":
        return "📝";
      default:
        return "⚠️";
    }
  }

  /**
   * Sort scenarios based on options.
   */
  private sortScenarios(testCases: TestCaseResult[]): TestCaseResult[] {
    if (this.options.sortScenarios === "alpha") {
      return [...testCases].sort((a, b) =>
        a.story.scenario.localeCompare(b.story.scenario)
      );
    }
    if (this.options.sortScenarios === "source") {
      return [...testCases].sort(bySourcePosition);
    }
    return testCases;
  }

  /**
   * Sort suite groups.
   */
  private sortSuiteGroups(
    entries: [string, TestCaseResult[]][]
  ): [string, TestCaseResult[]][] {
    if (this.options.sortScenarios === "alpha") {
      return entries.sort(([a], [b]) => a.localeCompare(b));
    }
    if (this.options.sortScenarios === "source") {
      return entries.sort(([, a], [, b]) => {
        return earliestSourceLine(a) - earliestSourceLine(b);
      });
    }
    return entries;
  }
}

/**
 * Group array items by a key function.
 */
function groupBy<T, K>(items: T[], keyFn: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  }
  return map;
}

/**
 * Label the feature the way it was declared.
 *
 * An ability reads as something a person can now do; a business need covers the
 * cross-cutting concerns nobody asks for by name, like security or performance.
 * Naming the kind in the heading keeps that distinction visible to a reader who
 * never sees the test code.
 */
function formatFeatureHeading(declared: FeatureDeclaration): string {
  const label =
    declared.kind === "ability"
      ? "Ability"
      : declared.kind === "business-need"
        ? "Business Need"
        : "Feature";
  return `${label}: ${declared.title}`;
}

/**
 * Strip the leading indentation a template literal picks up from its source.
 *
 * Narratives are written inline in test files, so they arrive indented to match
 * the surrounding code. Left alone, four spaces would make Markdown render the
 * whole paragraph as a code block.
 */
function dedentNarrative(narrative: string): string {
  const lines = narrative.replace(/\t/g, "  ").split("\n");
  const indents = lines
    .filter((line) => line.trim().length > 0)
    .map((line) => line.length - line.trimStart().length);
  const shortest = indents.length > 0 ? Math.min(...indents) : 0;
  return lines
    .map((line) => line.slice(shortest))
    .join("\n")
    .trim();
}
